import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EmailTemplate } from 'src/app/models/email-template';
import { GroupsService } from 'src/app/services/groups.service';

interface EmailTemplateUpdateResult {
    emailContentHtml: string;
    fromAddress: string;
    replyTo: string;
    subject: string;
    logoAlignment: number;
}

interface LogoUploadResponse {
    message: string;
}

// Services
import { SharedService } from '../../../layouts/shared-service';
import { CustomerinfobygroupService } from '../../../services/customerinfobygroup.service';
import { EmailcontentService } from '../../../services/emailcontent.service';
import { FbosService } from '../../../services/fbos.service';
import { GroupAnalyticsEmailTemplateDialogComponent } from '../group-analytics-email-template-dialog/group-analytics-email-template-dialog.component';
import {
    GroupAnalyticsGenerateDialogComponent,
    GroupAnalyticsGenerateDialogData,
} from '../group-analytics-generate-dialog/group-analytics-generate-dialog.component';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { AlignmentEnum } from 'src/app/enums/logo-alignment.enum';
import { GroupCustomerCaptureRateComponent } from '../group-customer-capture-rate/group-customer-capture-rate.component';
import { AppDialogWrapperService } from '../../../ni-components/app-dialog-wrapper/app-dialog-wrapper.service';
import { ManageScheduledEmailsComponent } from '../manage-scheduled-emails/manage-scheduled-emails.component';

@Component({
    selector: 'app-group-analytics-home',
    styleUrls: ['./group-analytics-home.component.scss'],
    templateUrl: './group-analytics-home.component.html',
})
export class GroupAnalyticsHomeComponent implements OnInit {
    pageTitle = 'Group Analytics';

    customers: any[];
    fbos: any[];
    emailTemplate: EmailTemplate = null;

    logoUrl = '';
    chartName = 'Group Analytics';
    logoAlignment = 0;

    constructor(
        private sharedService: SharedService,
        private fbosService: FbosService,
        private groupsService: GroupsService,
        private customerInfoByGroupService: CustomerinfobygroupService,
        private emailContentService: EmailcontentService,
        private ngxLoader: NgxUiLoaderService,
        private appDialogWrapperService: AppDialogWrapperService
    ) {}

    ngOnInit() {      
        this.loadCustomers();
        this.loadFbos();
        this.loadEmailTemplate();
        this.loadLogo();
    }

    onGenerate() {
        const dialogWrapper = this.appDialogWrapperService.open({
            title: 'FBO Group Customer Pricing Report',
            component: GroupAnalyticsGenerateDialogComponent,
            data: {
                customers: this.customers,
                emailTemplate: this.emailTemplate,
            },
            width: '500px',
            height: '800px',
        });

        dialogWrapper.afterClosed().subscribe((result) => {
            // Dialog closed - reload email template if needed
            this.loadEmailTemplate();
        });
    }

    onEditEmail() {
        const dialogWrapper = this.appDialogWrapperService.open({
            title: 'Email Template',
            component: GroupAnalyticsEmailTemplateDialogComponent,
            data: {
                emailContentHtml: this.emailTemplate?.emailContentHtml,
                fromAddress: this.emailTemplate?.fromAddress,
                logoUrl: this.logoUrl,
                replyTo: this.emailTemplate?.replyTo,
                subject: this.emailTemplate?.subject,
                logoAlignment: this.emailTemplate?.logoAlignment,
            },
            width: '500px',
            height: '630px',
        });

        dialogWrapper.afterClosed().subscribe((result: EmailTemplateUpdateResult) => {
            if (!result) {
                return; // User cancelled
            }
            this.handleEmailTemplateUpdate(result);
        });
    }

    private handleEmailTemplateUpdate(result: EmailTemplateUpdateResult): void {
        if (!result) {
            return;
        }

        const emailTemplate: EmailTemplate = {
            emailContentHtml: result.emailContentHtml,
            fromAddress: result.fromAddress,
            groupId: this.sharedService.currentUser.groupId,
            oid: this.emailTemplate?.oid,
            replyTo: result.replyTo,
            subject: result.subject,
            logoAlignment: result.logoAlignment,
        };

        if (!this.emailTemplate || !this.emailTemplate.oid) {
            this.emailContentService
                .add(emailTemplate)
                .subscribe((data: EmailTemplate) => {
                    this.emailTemplate = data;
                });
        } else {
            this.emailContentService
                .update(emailTemplate)
                .subscribe((data: EmailTemplate) => {
                    this.emailTemplate = emailTemplate;
                });
        }
    }
    public manageScheduledEmails() {
        const dialogWrapper = this.appDialogWrapperService.open({
            title: 'Manage Scheduled Emails',
            component: ManageScheduledEmailsComponent,
            data: {
                customers: this.customers
            },
            width: '1300px',
            height: 'auto'
        });

        // Subscribe to the generateEmail event from the component instance
        dialogWrapper.dialogRef.afterOpened().subscribe(() => {
            const wrapperComponent = dialogWrapper.dialogRef.componentInstance as any;
            const manageEmailsComponent = wrapperComponent?.getComponentInstance?.() as ManageScheduledEmailsComponent;
            
            if (manageEmailsComponent && manageEmailsComponent.generateEmail) {
                manageEmailsComponent.generateEmail.subscribe(() => {
                    // Close the dialog and trigger onGenerate
                    dialogWrapper.close();
                    this.onGenerate();
                });
            }
        });
    }

    onOpenGroupCustomerCaptureRate() {
        const dialogRef = this.appDialogWrapperService.open({
            title: 'Group Customer Capture Rate',
            component: GroupCustomerCaptureRateComponent,
            width: '1500px',
            maxWidth: '95vw',
        });

        dialogRef.afterClosed().subscribe((result) => {
            // Handle dialog close if needed
        });
    }

    private loadCustomers() {
        this.ngxLoader.startLoader(this.chartName);

        this.customerInfoByGroupService
            .getCustomersWithContactsByGroup(
                this.sharedService.currentUser.groupId
            )
            .subscribe((customers: any[]) => {
                this.customers = customers;
                this.ngxLoader.stopLoader(this.chartName);
            });
    }

    private loadFbos() {
        this.fbosService
            .getForGroup(this.sharedService.currentUser.groupId)
            .subscribe((fbos: any[]) => {
                this.fbos = fbos;
            });
    }

    private loadEmailTemplate() {
        this.emailContentService
            .getForGroup(this.sharedService.currentUser.groupId)
            .subscribe((data: EmailTemplate) => {
                this.emailTemplate = data;
                if (!this.emailTemplate) {
                    this.emailTemplate = {
                        emailContentHtml: '',
                        fromAddress: '',
                        groupId: this.sharedService.currentUser.groupId,
                        subject: '',
                    };
                    this.logoAlignment = AlignmentEnum.CENTER;
                }
            });
    }

    private loadLogo() {
        this.groupsService
            .getLogo(this.sharedService.currentUser.groupId)
            .subscribe((logoData: LogoUploadResponse) => {
                this.logoUrl = logoData.message;
            });
    }
}
